import axiosInstance from "../api.axiosInstance"
import type {
  ArcadiaGreenButtonConnectionDto,
  ArcadiaGreenButtonCsvCommitResultDto,
  ArcadiaGreenButtonCsvPreviewResultDto,
  ArcadiaGreenButtonDiagnosticDto,
  ArcadiaGreenButtonProposalApplicationDto,
  ArcadiaGreenButtonRecoveryResultDto,
  ArcadiaGreenButtonSiteDataResponseDto,
  CommitArcadiaGreenButtonCsvRecoveryRequestDto,
  ConfirmArcadiaGreenButtonUsageRequestDto,
  StartArcadiaGreenButtonConnectionResponseDto,
} from "../dto/arcadia-green-button.dto"

export class ArcadiaGreenButtonService {
  async fetchSiteData(
    siteId: string,
    proposalId: string,
  ): Promise<ArcadiaGreenButtonSiteDataResponseDto> {
    const response =
      await axiosInstance.get<ArcadiaGreenButtonSiteDataResponseDto>(
        `/sites/${encodeURIComponent(siteId)}/green-button`,
        { params: { proposalId } },
      )

    return response.data
  }

  async startConnection(
    siteId: string,
    proposalId: string,
  ): Promise<StartArcadiaGreenButtonConnectionResponseDto> {
    const response =
      await axiosInstance.post<StartArcadiaGreenButtonConnectionResponseDto>(
        `/sites/${encodeURIComponent(siteId)}/green-button/connections`,
        { proposalId },
      )

    return response.data
  }

  async linkExistingProject(
    siteId: string,
    powurProjectId: number,
    proposalId: string,
  ): Promise<ArcadiaGreenButtonSiteDataResponseDto> {
    const response =
      await axiosInstance.post<ArcadiaGreenButtonSiteDataResponseDto>(
        `/sites/${encodeURIComponent(siteId)}/green-button/links`,
        { powurProjectId, proposalId },
      )

    return response.data
  }

  async resendConnectionEmail(
    siteId: string,
    connectionId: string,
    proposalId: string,
  ): Promise<StartArcadiaGreenButtonConnectionResponseDto> {
    const response =
      await axiosInstance.post<StartArcadiaGreenButtonConnectionResponseDto>(
        `/sites/${encodeURIComponent(
          siteId,
        )}/green-button/connections/${encodeURIComponent(connectionId)}/email`,
        { proposalId },
      )

    return response.data
  }

  async retryConnection(
    siteId: string,
    connectionId: string,
    proposalId: string,
    requestId: string,
  ): Promise<ArcadiaGreenButtonConnectionDto> {
    const response = await axiosInstance.post<ArcadiaGreenButtonConnectionDto>(
      `/sites/${encodeURIComponent(siteId)}/green-button/connections/${encodeURIComponent(
        connectionId,
      )}/retry`,
      { proposalId, requestId },
    )

    return response.data
  }

  async selectMeter(
    siteId: string,
    connectionId: string,
    meterId: string,
    proposalId: string,
  ): Promise<ArcadiaGreenButtonConnectionDto> {
    const response = await axiosInstance.post<ArcadiaGreenButtonConnectionDto>(
      `/sites/${encodeURIComponent(siteId)}/green-button/connections/${encodeURIComponent(
        connectionId,
      )}/meter-selection`,
      { meterId, proposalId },
    )

    return response.data
  }

  async resumeProposalUsage(siteId: string, proposalId: string): Promise<void> {
    await axiosInstance.post(
      `/sites/${encodeURIComponent(siteId)}/green-button/proposals/${encodeURIComponent(
        proposalId,
      )}/resume`,
    )
  }

  async fetchSupportDiagnostic(
    siteId: string,
    connectionId: string,
  ): Promise<ArcadiaGreenButtonDiagnosticDto> {
    const response = await axiosInstance.get<ArcadiaGreenButtonDiagnosticDto>(
      `/v2/arcadia/green-button/sites/${encodeURIComponent(
        siteId,
      )}/connections/${encodeURIComponent(connectionId)}/diagnostic`,
    )
    return response.data
  }

  async recoverSupportConnection(
    siteId: string,
    connectionId: string,
    requestId: string,
  ): Promise<ArcadiaGreenButtonRecoveryResultDto> {
    const response =
      await axiosInstance.post<ArcadiaGreenButtonRecoveryResultDto>(
        `/v2/arcadia/green-button/sites/${encodeURIComponent(
          siteId,
        )}/connections/${encodeURIComponent(connectionId)}/recovery`,
        { requestId },
      )
    return response.data
  }

  async previewCsvRecovery(
    siteId: string,
    connectionId: string,
    file: File,
    requestId: string,
  ): Promise<ArcadiaGreenButtonCsvPreviewResultDto> {
    const formData = new FormData()
    formData.append(`file`, file)
    formData.append(`requestId`, requestId)

    const response =
      await axiosInstance.post<ArcadiaGreenButtonCsvPreviewResultDto>(
        `/v2/arcadia/green-button/sites/${encodeURIComponent(
          siteId,
        )}/connections/${encodeURIComponent(connectionId)}/csv/preview`,
        formData,
        // Clears the instance's default `application/json` header so axios leaves
        // the FormData body untouched and the browser sets the multipart boundary.
        { headers: { "Content-Type": undefined } },
      )
    return response.data
  }

  async commitCsvRecovery(
    siteId: string,
    connectionId: string,
    file: File,
    input: CommitArcadiaGreenButtonCsvRecoveryRequestDto,
  ): Promise<ArcadiaGreenButtonCsvCommitResultDto> {
    const formData = new FormData()
    formData.append(`file`, file)
    formData.append(`requestId`, input.requestId)
    formData.append(`expectedContentHash`, input.expectedContentHash)
    formData.append(`expectedEvidenceHash`, input.expectedEvidenceHash)

    const response =
      await axiosInstance.post<ArcadiaGreenButtonCsvCommitResultDto>(
        `/v2/arcadia/green-button/sites/${encodeURIComponent(
          siteId,
        )}/connections/${encodeURIComponent(connectionId)}/csv/commit`,
        formData,
        // Clears the instance's default `application/json` header so axios leaves
        // the FormData body untouched and the browser sets the multipart boundary.
        { headers: { "Content-Type": undefined } },
      )
    return response.data
  }

  async confirmProposalUsage(
    siteId: string,
    proposalId: string,
    input: ConfirmArcadiaGreenButtonUsageRequestDto,
  ): Promise<ArcadiaGreenButtonProposalApplicationDto> {
    const response =
      await axiosInstance.post<ArcadiaGreenButtonProposalApplicationDto>(
        `/sites/${encodeURIComponent(siteId)}/green-button/proposals/${encodeURIComponent(
          proposalId,
        )}/confirmation`,
        input,
      )

    return response.data
  }
}

export const arcadiaGreenButtonService = new ArcadiaGreenButtonService()
